// Books' in-module hops: plain router links and navigation to the package's own addresses
// (`BOOKS_ROUTES`), writing no history state — the shell carries the frame (org-console-shell §
// The sidebar on every page). A statement's scope is the address's search, so it can be linked to.
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { booksPath, type BooksView } from './books-routes';

export type BooksSearch = Record<string, string | number>;

function cleanSearch(search: Record<string, string | number | undefined> | undefined): BooksSearch {
  const out: BooksSearch = {};
  for (const [name, value] of Object.entries(search ?? {})) {
    if (value !== undefined && value !== '') out[name] = value;
  }
  return out;
}

export function BooksLink({
  view,
  search,
  children,
  ...props
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  view: BooksView;
  search?: Record<string, string | number | undefined>;
  children: ReactNode;
}) {
  // The router marks a link the current page at its EXACT address only. By default it marks every
  // link whose path is a prefix of the current one, and spreads `aria-current` over the caller's:
  // the chart of accounts' tab, or an account's way back to it, would read as the page you are on.
  return (
    <Link
      {...props}
      to={booksPath(view)}
      search={cleanSearch(search)}
      activeOptions={{ exact: true }}
    >
      {children}
    </Link>
  );
}

export function useBooksNavigate(): (
  view: BooksView,
  search?: Record<string, string | number | undefined>,
) => void {
  const navigate = useNavigate();
  return (view, search) => void navigate({ to: booksPath(view), search: cleanSearch(search) });
}

/** The address's search, read tolerantly: a value is a string or a number, or it is absent. */
export function useBooksSearch(): (name: string) => string | undefined {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  return (name) => {
    const value = search[name];
    if (typeof value === 'string') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return undefined;
  };
}
